import { ButtonDesigns, ButtonTypes } from '@paywithextend/components'
import cn from 'classnames'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import DecliningBalanceFlyoutSvg from 'ext-ui/svg/illustration/DecliningBalanceFlyoutSvg'
import DigitalCreditCardFlyoutSvg from 'ext-ui/svg/illustration/DigitalCreditCardFlyoutSvg'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import Box from 'src/components/Box/Box'
import Button from 'src/components/Button/Button'
import ChooseObjectTypeBox from 'src/components/ChooseObjectTypeBox/ChooseObjectTypeBox'
import ForVendorsAndContractorsTooltip from 'src/components/ForVendorsAndContractorsTooltip/ForVendorsAndContractorsTooltip'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { ImageNames, VirtualCardTypes } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import {
  createVirtualCardUrl,
  registerOrganizationUrl,
} from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { useQueryOrganizationVirtualCardTypes } from 'src/queries/organizations/virtualCardTypes/virtualCardTypes.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { selectAppName } from 'src/redux/selectors/session.selectors'

import style from './VirtualCardTypePicker.style'

interface VirtualCardTypePickerProps {
  className?: string
  setScreen: (val: CreateVirtualCardScreen) => void
  title?: React.ReactNode
  pickerSubtitle?: React.ReactNode
  userHasOrg: boolean
  standardDisabled?: boolean
  recurringDisabled?: boolean
  decliningBalanceDisabled?: boolean
  issuerCycleDisabled?: boolean
  issuerCycleDescription?: string
  decliningBalanceDescription?: string
  guestAccessDisabled?: boolean
  hideRecurring?: boolean
  hideGuestCards?: boolean
  hasNoRCNs?: boolean
  noRCNsTitle?: React.ReactNode
  noRCNsBody?: React.ReactNode
}

const VirtualCardTypePicker: React.FC<VirtualCardTypePickerProps> = (props) => {
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()
  const title = <>{props.title ?? translate('Create a Virtual Card')}</>
  const pickerSubtitle = (
    <>
      {props.pickerSubtitle ??
        translate('Choose the type of card you need to create.')}
    </>
  )
  const user = useCurrentUser()
  const appName = useSelector(selectAppName)
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [StatisticsKeys.CreditCardsActiveDelegateCount] as const,
  })
  const hasBudgets =
    (statisticsResults[StatisticsKeys.CreditCardsActiveDelegateCount].data ??
      0) > 0

  const standardCardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeStandard,
  )
  const recurringCardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeRecurring,
  )
  const guestCardImage = useColorOrGrayscaleImage(ImageNames.CardTypeGuest)
  const recurringGuestCardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeRecurringGuest,
  )
  const noVirtualCardsImage = useColorOrGrayscaleImage(
    ImageNames.NoVirtualCards,
  )

  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled

  const {
    data: virtualCardTypes,
    isLoading: isLoadingCardTypes,
    isError: isCardTypesError,
  } = useQueryOrganizationVirtualCardTypes({
    orgId: user?.organizationId,
    enabled: user?.organizationId !== undefined,
  })

  const disabledWithBudgets = !props.userHasOrg && hasBudgets
  const disabledWithoutBudgets = !props.userHasOrg && !hasBudgets

  if (isLoadingCardTypes) {
    return (
      <ActionRightFlyoutContent
        css={style}
        className={cn('VirtualCardTypePicker', props.className)}
        title={title}
        pickerSubtitle={pickerSubtitle}
      >
        <LoadingBackgroundPage />
      </ActionRightFlyoutContent>
    )
  }

  if (isCardTypesError) {
    return (
      <ActionRightFlyoutContent
        css={style}
        className={cn('VirtualCardTypePicker', props.className)}
        title={title}
        pickerSubtitle={pickerSubtitle}
      >
        <AlertPanel variant="warning">
          <div className="alert-text">
            {translate(
              'An error occurred. Please refresh your browser and check your internet connection.',
            )}
          </div>
        </AlertPanel>
      </ActionRightFlyoutContent>
    )
  }

  const showStandard = virtualCardTypes?.includes(VirtualCardTypes.standard)
  const showRecurring =
    virtualCardTypes?.includes(VirtualCardTypes.recurring) &&
    !props.hideRecurring
  const showDecliningBalance = virtualCardTypes?.includes(
    VirtualCardTypes.decliningBalance,
  )
  const showIssuerCycle = virtualCardTypes?.includes(
    VirtualCardTypes.issuerCycle,
  )

  const hasNoCardTypes = virtualCardTypes?.length === 0

  if (hasNoCardTypes) {
    return (
      <ActionRightFlyoutContent
        css={style}
        className={cn('VirtualCardTypePicker', props.className)}
        title={title}
        pickerSubtitle={pickerSubtitle}
      >
        <div className="no-rcns-empty-state">
          <img src={noVirtualCardsImage} alt="" />
          <span className="no-rcns-title">{props.noRCNsTitle}</span>
          <span className="no-rcns-body">{props.noRCNsBody}</span>
        </div>
      </ActionRightFlyoutContent>
    )
  }

  return (
    <ActionRightFlyoutContent
      css={style}
      className={cn('VirtualCardTypePicker', props.className)}
      title={title}
      pickerSubtitle={pickerSubtitle}
    >
      {disabledWithoutBudgets && (
        <AlertPanel variant="warning">
          <div className="alert-text">
            <div className="alert-without-budgets-text">
              {translate(
                "Please confirm your org and team to keep using these cards (we've filled things out for you to speed things up).",
              )}
            </div>
            <div className="alert-without-budgets-button-container">
              <Button
                className="alert-without-budgets-button"
                linkProps={{
                  to: registerOrganizationUrl(),
                  ariaLabelSource: 'Review and confirm org',
                }}
                design={ButtonDesigns.designCta}
                type={ButtonTypes.button}
                text={<>{translate('Review and confirm org')}</>}
              />
            </div>
          </div>
        </AlertPanel>
      )}
      {disabledWithBudgets && (
        <AlertPanel variant="warning" className="alert-with-budgets">
          <div className="alert-text">
            {translate(
              'You will need to be in an organization in order to create cards. Contact your admin to get invited to their org.',
            )}
          </div>
        </AlertPanel>
      )}
      {showStandard && (
        <ChooseObjectTypeBox
          className={cn('card-type-box', {
            'card-type-box-org-disabled': !props.userHasOrg,
          })}
          imageSrc={standardCardImage}
          title={<>{translate('Standard Card')}</>}
          subtitle={
            <>
              {translate(
                'Create a basic virtual card with all the set controls you need to securely send a company card.',
              )}
            </>
          }
          onClick={(): void => {
            props.setScreen(CreateVirtualCardScreen.Standard)
            analytics?.track(
              'create virtual card flyout - create standard virtual card - button press',
              {
                interaction: 'button press',
                intent: 'create standard virtual card',
                screen: createVirtualCardUrl(),
              },
            )
          }}
          disabled={props.standardDisabled}
          id="standardCard"
        />
      )}
      {showRecurring && (
        <ChooseObjectTypeBox
          className={cn('card-type-box', {
            'card-type-box-org-disabled': !props.userHasOrg,
          })}
          imageSrc={recurringCardImage}
          title={<>{translate('Recurring Card')}</>}
          subtitle={
            <>
              {translate(
                'Set up a card for recurring expenses at a set frequency, such as monthly or weekly subscriptions.',
              )}
            </>
          }
          onClick={(): void => {
            props.setScreen(CreateVirtualCardScreen.Recurring)
            analytics?.track(
              'create virtual card flyout - create recurring virtual card - button press',
              {
                interaction: 'button press',
                intent: 'create recurring virtual card',
                screen: createVirtualCardUrl(),
              },
            )
          }}
          disabled={props.recurringDisabled}
          id="recurringCard"
        />
      )}
      {showIssuerCycle && (
        <Box
          className={cn('card-type-box', 'issuer-cycle-box', {
            'card-type-box-org-disabled': !props.userHasOrg,
            disabled: props.issuerCycleDisabled,
          })}
          onClick={
            props.issuerCycleDisabled
              ? undefined
              : (): void => {
                  props.setScreen(CreateVirtualCardScreen.IssuerCycle)
                  analytics?.track(
                    'create virtual card flyout - create recurring card - button press',
                    {
                      interaction: 'button press',
                      intent: 'create recurring card',
                      screen: createVirtualCardUrl(),
                    },
                  )
                }
          }
          aria-disabled={props.issuerCycleDisabled}
          data-testid="issuerCycleCard"
          id="issuerCycleCard"
        >
          <DigitalCreditCardFlyoutSvg className="issuer-cycle-image" />
          <div className="issuer-cycle-text">
            <span className="issuer-cycle-title">
              {translate('Recurring Card')}
              {!props.issuerCycleDisabled && <SvgChevronRight />}
            </span>
            <span className="issuer-cycle-subtitle">
              {props.issuerCycleDescription ??
                translate(
                  'Create a recurring card with a recurring credit limit. Available credit resets automatically at the end of each billing cycle.',
                )}
            </span>
          </div>
        </Box>
      )}
      {showDecliningBalance && (
        <Box
          className={cn('card-type-box', 'declining-balance-box', {
            'card-type-box-org-disabled': !props.userHasOrg,
            disabled: props.decliningBalanceDisabled,
          })}
          onClick={
            props.decliningBalanceDisabled
              ? undefined
              : (): void => {
                  props.setScreen(CreateVirtualCardScreen.DecliningBalance)
                  analytics?.track(
                    'create virtual card flyout - create declining balance virtual card - button press',
                    {
                      interaction: 'button press',
                      intent: 'create declining balance virtual card',
                      screen: createVirtualCardUrl(),
                    },
                  )
                }
          }
          aria-disabled={props.decliningBalanceDisabled}
          id="decliningBalanceCard"
        >
          <DecliningBalanceFlyoutSvg className="declining-balance-image" />
          <div className="declining-balance-text">
            <span className="declining-balance-title">
              {translate('Declining Balance Card')}
              {!props.decliningBalanceDisabled && <SvgChevronRight />}
            </span>
            <span className="declining-balance-subtitle">
              {props.decliningBalanceDescription ??
                translate(
                  'Create a virtual card with a fixed spending limit. The available balance decreases as purchases are made and the card remains active until the date you set.',
                )}
            </span>
          </div>
        </Box>
      )}
      {/* TODO: Guest cards are still driven by the guestRoleEnabled feature flag rather than
          the virtualcardtypes API. Consider migrating to API-driven visibility for consistency. */}
      {!props.hideGuestCards && !showGuestRole && (
        <>
          <div className="vendor-section-header">
            {translate('For vendors and contractors')}
            <ForVendorsAndContractorsTooltip openLeft={false} />
          </div>
          <ChooseObjectTypeBox
            className={cn('card-type-box', {
              'card-type-box-org-disabled': !props.userHasOrg,
            })}
            imageSrc={guestCardImage}
            title={<>{translate('Guest Card')}</>}
            subtitle={
              <>
                {translate(
                  'Send virtual cards to people outside of ${appName} via email with a secure link.',
                  { appName },
                )}
              </>
            }
            onClick={(): void => {
              props.setScreen(CreateVirtualCardScreen.GuestAccess)
              analytics?.track(
                'create virtual card flyout - create guest access virtual card - button press',
                {
                  interaction: 'button press',
                  intent: 'create guest access virtual card',
                  screen: createVirtualCardUrl(),
                },
              )
            }}
            disabled={props.guestAccessDisabled}
            id="guestAccessCard"
          />
          <ChooseObjectTypeBox
            className={cn('card-type-box', {
              'card-type-box-org-disabled': !props.userHasOrg,
            })}
            imageSrc={recurringGuestCardImage}
            title={<>{translate('Recurring Guest Card')}</>}
            subtitle={
              <>
                {translate(
                  'Set up a card for recurring expenses for people outside of ${appName} at a set frequency.',
                  { appName },
                )}
              </>
            }
            onClick={(): void => {
              props.setScreen(CreateVirtualCardScreen.RecurringGuest)
              analytics?.track(
                'create virtual card flyout - create recurring guest access virtual card - button press',
                {
                  interaction: 'button press',
                  intent: 'create recurring guest access virtual card',
                  screen: createVirtualCardUrl(),
                },
              )
            }}
            disabled={props.guestAccessDisabled}
            id="recurringGuestAccessCard"
          />
        </>
      )}
    </ActionRightFlyoutContent>
  )
}

export default VirtualCardTypePicker
